import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  computed,
  inject,
  input,
} from '@angular/core';

import { filter, map, NEVER, Observable, of } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { finShare } from '@fcom/rx';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  IconPosition,
  NotificationLayout,
  NotificationTheme,
  TabLayoutType,
  TabTheme,
} from '@fcom/ui-components';
import { isShortHaulFlight } from '@fcom/common';
import { equals } from '@fcom/core/utils';
import {
  Category,
  FinnairBoundItem,
  FinnairCart,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import {
  BoundWithServices,
  ModifiedServiceInfo,
  ModifiedServiceProgress,
  UpsellCategoriesForBound,
} from '@fcom/dapi/interfaces';
import { ConfigService } from '@fcom/core/index';

import { getServicesForBoundWithUpsell } from '../../../../utils/common-booking.utils';
import { SmpProduct } from '../product-list/product-list.component';
import { CartOrOrder } from '../../../../interfaces';
import { AllowedActionsMode } from '../../enums';
import { OrderPartStatus } from '../../../../store';

@Component({
  selector: 'fin-included-products',
  templateUrl: './included-products.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IncludedProductsComponent implements OnInit, OnChanges {
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);

  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly Category = Category;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TabLayoutType = TabLayoutType;
  readonly TabTheme = TabTheme;
  readonly AllowedActionsMode = AllowedActionsMode;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;

  @Input() booking: FinnairCart | FinnairOrder;

  @Input() selectedPassengers: FinnairPassengerItem[];

  @Input() bound: FinnairBoundItem | undefined;

  @Input() showHeading = true;

  @Input() showTickets = false;

  @Input() showModifyButtons: Category[] = [];

  @Input() showInformationModal = false;

  @Input() useCabinClass = false;

  @Input() titleTranslationKey;

  @Input() showAdditionalTitle = false;

  @Input() expandOpen = false;

  @Input() headerContentTranslationKey?: string;

  @Input() servicesToShow: FinnairServiceItem[];

  @Input() showAsPending = false;

  @Input() upsellInModal$: Observable<UpsellCategoriesForBound> = NEVER;

  @Input() headingClasses = 'font-heading-4 font-title-medium';

  @Input() showUpsell = false;

  @Input() categoriesWithUpsell: Category[] = undefined;

  @Input() displaySeatReassignMessage = false;

  @Input() includeSeatActionButtons = false;

  @Input() isCarrierConnect = false;

  @Input() showNonAYSeats = false;

  @Input() includeAssignAtAirportStatus = false;

  @Input() groupServicesByBound = false;

  @Input() products$: Observable<SmpProduct[]> = of([]);

  @Input() showPendingServicesInTitles = false;

  @Input() mode: AllowedActionsMode = AllowedActionsMode.INFO;

  @Input() categoryStatus: Partial<Record<Category, OrderPartStatus>> = {};

  showQuotaTag = input(false);
  addSelectedItemsToQuota = input(false);

  serviceModifyProgress = input<ModifiedServiceProgress>({
    modifyInProgress: false,
  });

  @Output()
  continuePurchase: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  removeProduct: EventEmitter<ModifiedServiceInfo> = new EventEmitter<ModifiedServiceInfo>();

  @Output()
  selectProduct: EventEmitter<ModifiedServiceInfo> = new EventEmitter<ModifiedServiceInfo>();

  @Output()
  viewSeatmap: EventEmitter<void> = new EventEmitter();

  servicesForBounds: BoundWithServices[];

  headerContents$: Observable<string[]>;

  isShortHaulFlight: boolean;

  unpaidServices: boolean;

  isCaobCheckinSalesEnabled: boolean;

  ngOnInit(): void {
    this.updateServices();

    this.headerContents$ = this.headerContentTranslationKey
      ? this.languageService.translate(this.headerContentTranslationKey).pipe(
          map((content) => (Array.isArray(content) ? content : [content])),
          filter(Boolean),
          finShare()
        )
      : NEVER;

    this.isShortHaulFlight = this.isShortHaul(this.booking);
    this.isCaobCheckinSalesEnabled = this.configService.cfg.enableCaobCheckinSales;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !equals(changes['showUpsell']?.currentValue, changes['showUpsell']?.previousValue) ||
      !equals(changes['showUpsellPaymentButton']?.currentValue, changes['showUpsellPaymentButton']?.previousValue) ||
      !equals(changes['servicesToShow']?.currentValue, changes['servicesToShow']?.previousValue) ||
      !equals(changes['booking']?.currentValue, changes['booking']?.previousValue) ||
      !equals(changes['categoryStatus']?.currentValue, changes['categoryStatus']?.previousValue)
    ) {
      this.updateServices();
    }
  }

  private isShortHaul(booking: FinnairCart | FinnairOrder): boolean {
    const locations = Object.values(booking.locations);
    return isShortHaulFlight(locations[0], locations[1]);
  }

  private updateServices(): void {
    this.servicesForBounds = this.getServicesToShowForBounds(
      this.booking,
      this.showUpsell,
      this.servicesToShow,
      this.bound ? [this.bound] : this.booking.bounds
    );
    this.unpaidServices = this.servicesForBounds
      .flatMap((bound) => bound.services)
      .some((service) => service.unpaidQuantity > 0);
  }

  private getServicesToShowForBounds(
    cartOrOrder: CartOrOrder,
    showUpsell: boolean,
    servicesToShow: FinnairServiceItem[],
    bounds: FinnairBoundItem[]
  ): BoundWithServices[] {
    return bounds.map((bound) => {
      const services = getServicesForBoundWithUpsell(
        cartOrOrder,
        showUpsell,
        servicesToShow,
        cartOrOrder.services.servicesOrder,
        bound,
        cartOrOrder.serviceCatalog,
        this.categoryStatus,
        this.categoriesWithUpsell
      );
      return {
        ...bound,
        services,
      } as BoundWithServices;
    });
  }

  onSelectProduct(serviceData: ModifiedServiceInfo, boundId: string): void {
    this.selectProduct.emit({
      ...serviceData,
      boundId,
    });
  }

  onContinuePurchase(): void {
    this.continuePurchase.emit();
  }

  onRemoveProduct(serviceData: ModifiedServiceInfo, boundId: string): void {
    this.removeProduct.emit({
      ...serviceData,
      boundId: boundId,
    });
  }

  showUnpaidNotification = computed(() => {
    return this.isCaobCheckinSalesEnabled && [AllowedActionsMode.MMB, AllowedActionsMode.CHECK_IN].includes(this.mode);
  });

  enableQuotaTag = computed(() => {
    return this.configService.cfg.enableAncillaryFrontendQuota && this.showQuotaTag();
  });
}
